import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { of, throwError } from "rxjs";
import { map, switchMap, tap } from "rxjs/operators";
import { PaymentMethod } from "src/app/model/checkout/payment-method";
import { DialogService } from "src/service/dialog.service";
import { StripeService } from "src/service/stripe.service";
import { WalletService } from "../../wallet/wallet.service";
import { CheckoutService } from "../checkout.service";

@Component({
  selector: "app-credit-card-form",
  templateUrl: "./credit-card-form.component.html",
  styleUrls: ["../../dashboard.style.scss"],
})
export class CreditCardFormComponent implements OnInit, OnDestroy {
  constructor(
    public checkoutService: CheckoutService,
    private dialogService: DialogService,
    private router: Router,
    private route: ActivatedRoute,
    public stripeService: StripeService,
    private walletService: WalletService
  ) {}

  stripe;
  paymentMethods = this.stripeService.getPaymentMethods();
  selectedPaymentMethod: PaymentMethod;
  mountedCard;
  creditCardForm: FormGroup;
  pendingSavingsPlanId;
  loading = false;
  card;
  needVaultPay;

  ngOnInit(): void {
    const vaultPayProduct = this.route.snapshot.queryParams["vaultPayProduct"];
    this.needVaultPay = vaultPayProduct == "true";

    this.checkoutService.pendingSavingsPlanId = null;

    this.creditCardForm = new FormGroup({
      cardList: new FormControl(),
    });

    this.creditCardForm
      .get("cardList")
      .valueChanges.subscribe(
        (paymentMethod: PaymentMethod) =>
          (this.selectedPaymentMethod = paymentMethod)
      );

    this.stripe = this.stripeService.stripe;

    let style = {
      base: {
        color: "#32325d",
        fontFamily: "Arial, sans-serif",
        fontSmoothing: "antialiased",
        fontSize: "16px",
        "::placeholder": {
          color: "#32325d",
        },
      },
      invalid: {
        fontFamily: "Arial, sans-serif",
        color: "#fa755a",
        iconColor: "#fa755a",
      },
    };

    let elements = this.stripe.elements();
    this.card = elements.create("card", { style: style });
    // Stripe injects an iframe into the DOM
    this.card.mount("#card-element");

    this.card.on("change", function (event) {
      // Disable the Pay button if there are no card details in the Element
      document.querySelector("button").disabled = event.empty;
    });
    let form = document.getElementById("payment-form");
    form.addEventListener("submit", (event) => this.onSubmit(event, this.card));
  }

  onSubmit(event, card) {
    this.loading = true;
    event.preventDefault();
    const { routerLink, queryParams, request, afterSuccess, afterCloseDialog } =
      this.checkoutService.selectedPaymentRequest.value;

    let pendingSavingsPlanId;

    request()
      .pipe(
        tap(() =>
          this.checkoutService.refreshCheckout({ paymentType: "CREDIT_CARD" })
        ),
        switchMap((response: any) => {
          if (response.clientSecret && response.clientSecretProductPrice) {
            return this.payWithCreditCard(
              response.clientSecretProductPrice
            ).pipe(
              switchMap(() => this.payWithCreditCard(response.clientSecret))
            );
          }
          if (response.clientSecret) {
            return this.payWithCreditCard(response.clientSecret);
          }
          if (response.clientSecretProductPrice) {
            return this.payWithCreditCard(response.clientSecretProductPrice);
          }

          return throwError("no client secret");
        }),
        tap(() => (this.loading = false)),
        switchMap((response:any) =>{
          if(response.error){
            return throwError(response.error)
          } else{
            return afterSuccess()
          }
        } ),
        switchMap(() => afterCloseDialog()),
        switchMap(() => this.walletService.syncWalletBalance())
      )
      .subscribe(
        () => {
          this.checkoutService.selectedShipment = null;
          this.router.navigate([routerLink], {
            relativeTo: this.route,
            queryParams,
          });
        },
        (error) => {
          this.checkoutService.selectedShipment = null;
          this.loading = false;
          this.checkoutService.pendingSavingsPlanId = pendingSavingsPlanId;
          this.dialogService.showErrorDialog(error.message);
        }
      );
  }

  payWithCreditCard(secretKey) {
    if (this.selectedPaymentMethod != null) {
      return this.stripeService.payWithId(
        this.selectedPaymentMethod.id,
        secretKey
      );
    } else {
      return this.stripeService.payWithCard(this.card, secretKey);
    }
  }

  ngOnDestroy(): void {
    this.checkoutService.pendingSavingsPlanId = null;
  }
}
